import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { investorContactAPI, distributorAPI, teamAPI } from '../services/api';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';

const salutations = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'Shri', 'Smt.'];

const InvestorContactForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    salutation: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    // Up to 2 extra emails — Add / Remove buttons append/pop items. Kept
    // separate from the primary `email` so all downstream send flows
    // (which target the primary) continue working unchanged.
    additional_emails: [],
    mobile_country_code: '+91',
    mobile: '',
    additional_mobiles: [],  // [{country_code, number}]
    telephone: '',
    additional_telephones: [],
    whatsapp_opt_out: 'No',
    distributor_id: '',
    investor_lead_id: '',
    brief_profile: '',
    educational_background: '',
    remarks: '',
    // Default flipped from 'No' to 'Yes' — every new contact lands on the
    // Fundraise Coverage roster until an admin explicitly opts them out.
    touch_base_required: 'Yes',
    coverage_urgency: 'No',
  });
  const [distributors, setDistributors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  // New distributor inline creation
  const [showNewDistributor, setShowNewDistributor] = useState(false);
  const [newDistName, setNewDistName] = useState('');

  useEffect(() => {
    loadDropdownData();
    if (isEdit) loadContact();
  }, [id]);

  const loadDropdownData = async () => {
    try {
      const [distRes, teamRes] = await Promise.all([
        distributorAPI.getAll(),
        teamAPI.getMembers(),
      ]);
      setDistributors(distRes.data);
      setTeamMembers(teamRes.data);
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
    }
  };

  const loadContact = async () => {
    try {
      const res = await investorContactAPI.getById(id);
      const c = res.data;
      setFormData({
        salutation: c.salutation || '',
        first_name: c.first_name || '',
        middle_name: c.middle_name || '',
        last_name: c.last_name || '',
        email: c.email || '',
        additional_emails: Array.isArray(c.additional_emails) ? c.additional_emails : [],
        mobile_country_code: c.mobile_country_code || '+91',
        mobile: c.mobile || '',
        additional_mobiles: Array.isArray(c.additional_mobiles) ? c.additional_mobiles : [],
        telephone: c.telephone || '',
        additional_telephones: Array.isArray(c.additional_telephones) ? c.additional_telephones : [],
        whatsapp_opt_out: c.whatsapp_opt_out || 'No',
        distributor_id: c.distributor_id || '',
        investor_lead_id: c.investor_lead_id || '',
        brief_profile: c.brief_profile || '',
        educational_background: c.educational_background || '',
        touch_base_required: c.touch_base_required || 'Yes',
        coverage_urgency: c.coverage_urgency || 'No',
      });
    } catch (err) {
      console.error('Failed to load contact:', err);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.first_name.trim()) errs.first_name = 'First name is required';
    if (!formData.last_name.trim()) errs.last_name = 'Last name is required';
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errs.email = 'Invalid email';
    // Salutation is intentionally optional — many investors (companies, trusts,
    // partnerships) don't have an applicable salutation.
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Clean empty strings to null for optional ID fields
      const cleanedData = { ...formData };
      if (!cleanedData.distributor_id) cleanedData.distributor_id = null;
      if (!cleanedData.investor_lead_id) cleanedData.investor_lead_id = null;
      if (!cleanedData.middle_name?.trim()) cleanedData.middle_name = null;
      if (!cleanedData.mobile?.trim()) {
        cleanedData.mobile = null;
        // Clear the country code too when the local number is blank — it
        // looks confusing on the table view otherwise.
        cleanedData.mobile_country_code = null;
      } else if (!cleanedData.mobile_country_code?.trim()) {
        cleanedData.mobile_country_code = '+91';
      }
      if (!cleanedData.telephone?.trim()) cleanedData.telephone = null;
      if (!cleanedData.brief_profile?.trim()) cleanedData.brief_profile = null;
      if (!cleanedData.educational_background?.trim()) cleanedData.educational_background = null;
      if (!cleanedData.remarks?.trim()) cleanedData.remarks = null;

      if (isEdit) {
        await investorContactAPI.update(id, cleanedData);
      } else {
        await investorContactAPI.create(cleanedData);
      }
      navigate('/investors/contacts');
    } catch (err) {
      console.error('Failed to save contact:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403) {
        alert('Session expired or insufficient permissions. Please re-login and try again.');
      } else if (status === 401) {
        alert('You are not authenticated. Please login again.');
      } else {
        alert(detail || 'Failed to save investor contact. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateDistributor = async () => {
    if (!newDistName.trim()) return;
    try {
      const res = await distributorAPI.create({ name: newDistName.trim() });
      setDistributors(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, distributor_id: res.data.id }));
      setNewDistName('');
      setShowNewDistributor(false);
    } catch (err) {
      console.error('Failed to create distributor:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div data-testid="investor-contact-form-page">
      <button
        onClick={() => navigate('/investors/contacts')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        data-testid="back-btn"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Investor Contacts
      </button>

      <div className="bg-white rounded-xl shadow-md">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="form-title">
            {isEdit ? 'Edit Investor Contact' : 'New Investor Contact'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Contact Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Salutation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salutation <span className="text-gray-400 text-xs">(optional)</span></label>
                <select
                  value={formData.salutation}
                  onChange={e => handleChange('salutation', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${errors.salutation ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500`}
                  data-testid="salutation-select"
                >
                  <option value="">Select</option>
                  {salutations.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {errors.salutation && <p className="text-red-500 text-xs mt-1">{errors.salutation}</p>}
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={e => handleChange('first_name', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${errors.first_name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500`}
                  data-testid="first-name-input"
                />
                {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
              </div>

              {/* Middle Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Middle Name</label>
                <input
                  type="text"
                  value={formData.middle_name}
                  onChange={e => handleChange('middle_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="middle-name-input"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={e => handleChange('last_name', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${errors.last_name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500`}
                  data-testid="last-name-input"
                />
                {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => handleChange('email', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500`}
                  data-testid="email-input"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                {/* Additional emails — capped at 2 to match the bulk-upload template */}
                {(formData.additional_emails || []).map((ev, i) => (
                  <div key={`addl-email-${i}`} className="flex gap-1.5 mt-1.5" data-testid={`additional-email-row-${i}`}>
                    <input
                      type="email"
                      value={ev}
                      onChange={(e) => {
                        setFormData((f) => {
                          const next = [...(f.additional_emails || [])];
                          next[i] = e.target.value;
                          return { ...f, additional_emails: next };
                        });
                      }}
                      placeholder={`Email ${i + 2}`}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      data-testid={`additional-email-input-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData((f) => ({
                        ...f,
                        additional_emails: (f.additional_emails || []).filter((_, idx) => idx !== i),
                      }))}
                      className="px-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove"
                      data-testid={`remove-additional-email-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(formData.additional_emails || []).length < 2 && (
                  <button
                    type="button"
                    onClick={() => setFormData((f) => ({
                      ...f,
                      additional_emails: [...(f.additional_emails || []), ''],
                    }))}
                    className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800"
                    data-testid="add-additional-email-btn"
                  >
                    + Add another email
                  </button>
                )}
              </div>

              {/* Mobile (split: Country Code + Local Number) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No</label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={formData.mobile_country_code}
                    onChange={e => handleChange('mobile_country_code', e.target.value)}
                    placeholder="+91"
                    className="w-20 border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-blue-500 text-center"
                    data-testid="mobile-country-code-input"
                  />
                  <input
                    type="text"
                    value={formData.mobile}
                    onChange={e => handleChange('mobile', e.target.value)}
                    placeholder="98765 43210"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    data-testid="mobile-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Split fields: country code (with +) and local number — ensures WhatsApp delivery for non-+91 numbers.</p>
                {/* Additional mobiles — each item is a {country_code, number} pair. */}
                {(formData.additional_mobiles || []).map((mv, i) => (
                  <div key={`addl-mobile-${i}`} className="flex gap-1.5 mt-1.5" data-testid={`additional-mobile-row-${i}`}>
                    <input
                      type="text"
                      value={mv?.country_code || ''}
                      onChange={(e) => {
                        setFormData((f) => {
                          const next = [...(f.additional_mobiles || [])];
                          next[i] = { ...(next[i] || {}), country_code: e.target.value };
                          return { ...f, additional_mobiles: next };
                        });
                      }}
                      placeholder="+91"
                      className="w-20 border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-blue-500 text-center"
                      data-testid={`additional-mobile-cc-${i}`}
                    />
                    <input
                      type="text"
                      value={mv?.number || ''}
                      onChange={(e) => {
                        setFormData((f) => {
                          const next = [...(f.additional_mobiles || [])];
                          next[i] = { ...(next[i] || {}), number: e.target.value };
                          return { ...f, additional_mobiles: next };
                        });
                      }}
                      placeholder={`Mobile ${i + 2}`}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      data-testid={`additional-mobile-input-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData((f) => ({
                        ...f,
                        additional_mobiles: (f.additional_mobiles || []).filter((_, idx) => idx !== i),
                      }))}
                      className="px-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove"
                      data-testid={`remove-additional-mobile-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(formData.additional_mobiles || []).length < 2 && (
                  <button
                    type="button"
                    onClick={() => setFormData((f) => ({
                      ...f,
                      additional_mobiles: [...(f.additional_mobiles || []), { country_code: '+91', number: '' }],
                    }))}
                    className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800"
                    data-testid="add-additional-mobile-btn"
                  >
                    + Add another mobile
                  </button>
                )}
              </div>

              {/* Telephone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telephone</label>
                <input
                  type="text"
                  value={formData.telephone}
                  onChange={e => handleChange('telephone', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="telephone-input"
                />
                {(formData.additional_telephones || []).map((tv, i) => (
                  <div key={`addl-tel-${i}`} className="flex gap-1.5 mt-1.5" data-testid={`additional-telephone-row-${i}`}>
                    <input
                      type="text"
                      value={tv}
                      onChange={(e) => {
                        setFormData((f) => {
                          const next = [...(f.additional_telephones || [])];
                          next[i] = e.target.value;
                          return { ...f, additional_telephones: next };
                        });
                      }}
                      placeholder={`Telephone ${i + 2}`}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                      data-testid={`additional-telephone-input-${i}`}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData((f) => ({
                        ...f,
                        additional_telephones: (f.additional_telephones || []).filter((_, idx) => idx !== i),
                      }))}
                      className="px-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      title="Remove"
                      data-testid={`remove-additional-telephone-${i}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(formData.additional_telephones || []).length < 2 && (
                  <button
                    type="button"
                    onClick={() => setFormData((f) => ({
                      ...f,
                      additional_telephones: [...(f.additional_telephones || []), ''],
                    }))}
                    className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800"
                    data-testid="add-additional-telephone-btn"
                  >
                    + Add another telephone
                  </button>
                )}
              </div>

              {/* WhatsApp Opt-out */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Opt-out</label>
                <select
                  value={formData.whatsapp_opt_out}
                  onChange={e => handleChange('whatsapp_opt_out', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="whatsapp-opt-out-select"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>

              {/* Distributor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Distributor</label>
                <div className="flex gap-2">
                  <select
                    value={formData.distributor_id}
                    onChange={e => handleChange('distributor_id', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    data-testid="distributor-select"
                  >
                    <option value="">Select Distributor</option>
                    {distributors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewDistributor(!showNewDistributor)}
                    className="px-2 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border text-gray-600"
                    title="Add new distributor"
                    data-testid="add-distributor-btn"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                {showNewDistributor && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={newDistName}
                      onChange={e => setNewDistName(e.target.value)}
                      placeholder="New distributor name"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="new-distributor-name"
                    />
                    <button
                      type="button"
                      onClick={handleCreateDistributor}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      data-testid="save-new-distributor-btn"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowNewDistributor(false); setNewDistName(''); }}
                      className="px-2 py-2 text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Investor Lead */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Investor Lead</label>
                <select
                  value={formData.investor_lead_id}
                  onChange={e => handleChange('investor_lead_id', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="investor-lead-select"
                >
                  <option value="">Select Team Member</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Long text fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brief Profile</label>
                <textarea
                  value={formData.brief_profile}
                  onChange={e => handleChange('brief_profile', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="brief-profile-textarea"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Educational Background</label>
                <textarea
                  value={formData.educational_background}
                  onChange={e => handleChange('educational_background', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="education-textarea"
                />
              </div>
            </div>
            {/* Remarks — internal-only free-form notes, spans full width so
                users have plenty of room to jot context (compliance, past
                interactions, family notes etc). */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={e => handleChange('remarks', e.target.value)}
                rows={5}
                placeholder="Free-form notes about this contact — visible only inside the platform."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                data-testid="remarks-textarea"
              />
            </div>
          </section>

          {/* Fundraise Coverage */}
          <section>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b">Fundraise Coverage</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Touch Base Required</label>
                <select
                  value={formData.touch_base_required}
                  onChange={e => handleChange('touch_base_required', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  data-testid="touch-base-select"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Urgency</label>
                <p className="text-sm text-gray-500 italic py-2">Auto-calculated from days since last interaction</p>
              </div>
            </div>
          </section>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate('/investors/contacts')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              data-testid="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center"
              data-testid="submit-btn"
            >
              <Save size={18} className="mr-2" />
              {submitting ? 'Saving...' : isEdit ? 'Update Contact' : 'Create Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvestorContactForm;
